/**
 * Developer Section
 * Code examples with syntax highlighting and copy functionality
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Check, Copy, Zap, Shield, Code2, Gauge } from 'lucide-react';
import { staggerContainerVariants, itemVariants } from '@/components/animations/variants';

interface CodeExample {
  id: string;
  name: string;
  language: string;
  code: string;
}

const codeExamples: CodeExample[] = [
  {
    id: 'curl',
    name: 'cURL',
    language: 'bash',
    code: `# Quick Start with cURL
curl https://api.naivana.io/v1/chat/completions \\
  -H "Authorization: Bearer $PATHFINDER_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {
        "role": "user",
        "content": "Explain quantum computing simply"
      }
    ],
    "temperature": 0.7,
    "max_tokens": 500
  }'

# Pathfinder automatically selects the best model
# Response includes routing metadata`,
  },
  {
    id: 'python',
    name: 'Python',
    language: 'python',
    code: `from pathfinder import Pathfinder

# Initialize client
client = Pathfinder(
    api_key="your_key_here",
    # Optional: custom routing policy
    policy="cost-optimized"
)

# Make a request
response = client.chat.completions.create(
    messages=[
        {
            "role": "user",
            "content": "Write a Python function to validate emails"
        }
    ],
    temperature=0.7,
    max_tokens=1000
)

# Access response
print(response.choices[0].message.content)

# View routing decision
print(f"Routed to: {response.model}")
print(f"Cost: {response.usage.total_cost:.4f}")
print(f"Latency: {response.routing.latency_ms}ms")`,
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    language: 'typescript',
    code: `import { Pathfinder } from '@pathfinder/sdk'

// Initialize with TypeScript support
const client = new Pathfinder({
  apiKey: process.env.PATHFINDER_API_KEY!,
  policy: 'balanced', // 'cost-optimized' | 'balanced' | 'performance'
})

// Type-safe request
const response = await client.chat.completions.create({
  messages: [
    {
      role: 'system',
      content: 'You are a helpful assistant.',
    },
    {
      role: 'user',
      content: 'Explain TypeScript generics',
    },
  ],
  temperature: 0.8,
  max_tokens: 1500,
})

// Fully typed response
console.log(response.choices[0].message.content)
console.log('Model:', response.model)
console.log('Cost: $' + response.usage.total_cost.toFixed(4))

// Access routing metadata
const routing = response.routing
console.log('Latency:', routing.latency_ms + 'ms')
console.log('Reasoning:', routing.reasoning.join(', '))`,
  },
  {
    id: 'openai',
    name: 'OpenAI Compatible',
    language: 'python',
    code: `from openai import OpenAI

# Drop-in replacement for OpenAI SDK
# Just change base_url and api_key
client = OpenAI(
    base_url="https://api.naivana.io/v1",
    api_key="your_pathfinder_key_here"
)

# Existing code works without changes
response = client.chat.completions.create(
    model="auto",  # Pathfinder auto-routes
    messages=[
        {
            "role": "user",
            "content": "Translate 'Hello' to Spanish"
        }
    ]
)

print(response.choices[0].message.content)

# Optional: Access Pathfinder-specific metadata
if hasattr(response, 'routing'):
    print(f"Routed to: {response.routing.selected_model}")
    print(f"Savings: {response.routing.savings_vs_baseline}%")`,
  },
];

const features = [
  {
    icon: <Zap className="w-5 h-5 text-yellow-500" />,
    title: 'Drop-in Replacement',
    description: 'OpenAI SDK compatible. No code refactoring needed.',
  },
  {
    icon: <Shield className="w-5 h-5 text-green-500" />,
    title: 'Type-Safe',
    description: 'Full TypeScript support with auto-completion.',
  },
  {
    icon: <Code2 className="w-5 h-5 text-primary" />,
    title: 'Multi-Language',
    description: 'Python, TypeScript, Go, Ruby, and more.',
  },
  {
    icon: <Gauge className="w-5 h-5 text-accent" />,
    title: 'Streaming Support',
    description: 'Real-time streaming responses for all models.',
  },
];

function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      {/* Copy Button */}
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 z-10"
        onClick={handleCopy}
      >
        {copied ? (
          <>
            <Check className="w-4 h-4 mr-1 text-green-500" />
            <span className="text-xs">Copied!</span>
          </>
        ) : (
          <>
            <Copy className="w-4 h-4 mr-1" />
            <span className="text-xs">Copy</span>
          </>
        )}
      </Button>

      {/* Code */}
      <SyntaxHighlighter
        language={language}
        style={oneDark}
        customStyle={{
          margin: 0,
          borderRadius: '0.75rem',
          fontSize: '0.875rem',
          padding: '1.5rem',
        }}
        showLineNumbers
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

export function DeveloperSection() {
  return (
    <section className="py-16 px-4 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Built for Developers
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Get started in minutes with our OpenAI-compatible SDK
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* Code Examples */}
          <Tabs defaultValue={codeExamples[0].id} className="mb-12">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              {codeExamples.map((example) => (
                <TabsTrigger key={example.id} value={example.id}>
                  {example.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {codeExamples.map((example) => (
              <TabsContent key={example.id} value={example.id}>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <CodeBlock code={example.code} language={example.language} />
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Features Grid */}
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={itemVariants}
          >
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-3">{feature.icon}</div>
                <h4 className="font-semibold mb-2">{feature.title}</h4>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            className="text-center mt-12"
            variants={itemVariants}
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" asChild>
                <a href="https://docs.naivana.io/" target="_blank" rel="noopener noreferrer">
                  View Full Documentation
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="https://docs.naivana.io/" target="_blank" rel="noopener noreferrer">
                  Explore API Reference
                </a>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
